
// components/Header/Header.jsx
import React, { useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import DarkModeToggle from "../darkModeToggle/DarkModeToggle";
import MobileDrawer from "../mobileDrawer/MobileDrawer";
import "./Header.css";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../sidebar/Sidebar";
import ConfirmModal from "../modals/ConfirmModal/ConfirmModal";
import { useMediaQuery } from "react-responsive";
import NotificationPanel from "../notificationPanel/NotificationPanel";
import { getImageSrc } from "../../utils/getImageSrc";

const Header = ({ notificationCount = 0, isMenuOpen, onMenuToggle }) => {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const handleOpenNotifications = () => setIsNotificationOpen(true);
  const handleCloseNotifications = () => setIsNotificationOpen(false);
  const handleToggleNotifications = () => setIsNotificationOpen((v) => !v);

  const isSmallMobile = useMediaQuery({ maxWidth: 820 });
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = !!user;

  const displayName = user
    ? user.academyName || `${user.firstName} ${user.lastName}`
    : "Guest";

  const avatarSrc = getImageSrc(user?.picture);

  const roleLabel =
    user?.role === "institution"
      ? "Training Center"
      : user?.role === "tutor"
        ? "Tutor"
        : user?.role === "student"
          ? "Student"
          : "";

  const floatingContactRef = useRef();
  const handleSupportClick = () => floatingContactRef.current?.openChat();

  const getLogoDestination = () => {
    const currentPath = location.pathname;
    if (
      currentPath.startsWith("/tutor-home") ||
      currentPath.startsWith("/academy-home") ||
      currentPath.startsWith("/student-home")
    )
      return "/";

    if (isAuthenticated) {
      switch (user.role) {
        case "tutor":       return "/tutor-home";
        case "institution": return "/academy-home";
        case "student":     return "/student-home";
        default:            return "/";
      }
    }
    return "/";
  };

  const logoDest = getLogoDestination();

  const isDashboardPage =
    isAuthenticated &&
    ["/tutor-home", "/academy-home", "/student-home"].some((path) =>
      location.pathname.startsWith(path),
    );

  const shouldOpenSidebar =
    !isAuthenticated || (isAuthenticated && isSmallMobile);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    setShowLogoutModal(false);
    navigate("/");
  };

  const handleLogoutClick = () => setShowLogoutModal(true);
  const cancelLogout = () => setShowLogoutModal(false);

  return (
    <>
      <header className="unified-header">
        <div className="header-container">

          {/* Logo — Link for instant navigation, no reload */}
          <div className="header-logo">
            <Link to={logoDest}>
              {/*
                Logo is in the LCP region (top of every page).
                fetchpriority="high" tells the browser to fetch it
                alongside the HTML, not after CSS/JS.
              */}
              <img
                src="/images/tutorslinks.png"
                alt="TutorsLinks"
                width={140}
                height={40}
                fetchPriority="high"
                decoding="sync"
              />
            </Link>
          </div>

          {/* Desktop nav — Link replaces <a> so React Router handles navigation
              client-side. No full page reload, no re-downloading assets. */}
          <nav className="nav-desktop">
            {!isAuthenticated ? (
              <>
                <Link to="/">Home</Link>
                <Link to="/blog">Blog and Resources</Link>
                <Link to="/about-us">About Us</Link>
                <Link to="/get-help">Get Help</Link>
              </>
            ) : (
              <>
                <Link to="/blog">Blog and Resources</Link>
                <Link to="/about-us">About Us</Link>
                <Link to="/get-help">Contact Us</Link>
              </>
            )}
          </nav>

          <div className="header-right">
            {!isAuthenticated ? (
              <div className="auth-buttons-desktop">
                <Link to="/login" className="login-btn">Login</Link>
                <Link to="/decision-screen" className="signup-btn">Create Account</Link>
              </div>
            ) : (
              <>
                <button
                  className="icon-btn contact-btn"
                  title="Support"
                  onClick={handleSupportClick}
                >
                  {/* Below-the-fold icon → lazy is fine */}
                  <img
                    src="/images/contact-us.png"
                    alt="Help"
                    width={24}
                    height={24}
                    loading="lazy"
                    decoding="async"
                  />
                </button>

                <NotificationPanel
                  userId={user?._id}
                  isOpen={isNotificationOpen}
                  onClose={handleCloseNotifications}
                  onToggle={handleToggleNotifications}
                />

                <div className="user-dropdown-container">
                  <button
                    className="avatar-btn"
                    title={displayName}
                    onClick={handleLogoutClick}
                  >
                    {/* User avatar: visible immediately on authenticated pages → eager */}
                    <img
                      src={avatarSrc}
                      alt={displayName}
                      className="avatar"
                      width={36}
                      height={36}
                      loading="eager"
                      decoding="sync"
                    />
                    <div className="user-info">
                      <span className="user-role">{roleLabel}</span>
                    </div>
                  </button>

                  {showDropdown && (
                    <div className="user-dropdown">
                      <Link to="/account" onClick={() => setShowDropdown(false)}>My Account</Link>
                      <Link to="/about-us" onClick={() => setShowDropdown(false)}>About Us</Link>
                      <Link to="/get-help" onClick={() => setShowDropdown(false)}>Help Desk</Link>
                      <hr />
                      <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                  )}
                </div>
              </>
            )}

            {!isAuthenticated && isSmallMobile && (
              <Link to="/decision-screen" className="mobile-signup-btn">
                Create Account
              </Link>
            )}

            {isSmallMobile && (
              <button
                className="mobile-menu-btn labeled"
                onClick={onMenuToggle}
              >
                <div className="hamburger-lines">
                  <span />
                  <span />
                  <span />
                </div>
                <p className="menu-text">Menu</p>
              </button>
            )}
          </div>
        </div>
      </header>


      {isDashboardPage
        ? null
        : shouldOpenSidebar && (
            <Sidebar
              isOpen={isMenuOpen}
              onToggle={onMenuToggle}
              onOpenNotifications={handleOpenNotifications}
            />
          )}

      <MobileDrawer
        isOpen={isMenuOpen}
        onClose={onMenuToggle}
        isAuthenticated={isAuthenticated}
        notificationCount={notificationCount}
        onOpenNotifications={handleOpenNotifications}
      />

      <ConfirmModal
        isOpen={showLogoutModal}
        title="Logout"
        message="Are you sure you want to logout?"
        onConfirm={handleLogout}
        onCancel={cancelLogout}
      />
    </>
  );
};

export default Header;
